import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { FaUser, FaEnvelope, FaPhone, FaCalendar, FaClock } from 'react-icons/fa';
import { IoMdArrowRoundBack } from 'react-icons/io';
import usersData from '../../../data/users.json';

const UserPageView = () => {
    const { userId } = useParams();
    const navigate = useNavigate();
    const [user, setUser] = useState<any>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        // Simular carregamento de dados
        setTimeout(() => {
            const foundUser = usersData.find(u => u.id === userId);
            setUser(foundUser);
            setLoading(false);
        }, 500);
    }, [userId]);

    const formatDate = (dateString: string) => {
        return new Date(dateString).toLocaleDateString('pt-BR', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'ACTIVE': return 'bg-green-500';
            case 'INACTIVE': return 'bg-red-500';
            default: return 'bg-gray-500';
        }
    };

    const getRoleLabel = (role: string) => {
        switch (role) {
            case 'ADMIN': return 'Administrador';
            case 'MLP_ANALYST': return 'Analista PLD';
            case 'USER': return 'Usuário';
            default: return role;
        }
    };

    if (loading) {
        return (
            <div className="p-6 space-y-4 animate-pulse">
                <div className="h-8 bg-gray-300 dark:bg-slate-600 rounded w-3/4"></div>
                <div className="space-y-3">
                    <div className="h-4 bg-gray-300 dark:bg-slate-600 rounded"></div>
                    <div className="h-4 bg-gray-300 dark:bg-slate-600 rounded w-5/6"></div>
                    <div className="h-4 bg-gray-300 dark:bg-slate-600 rounded w-4/6"></div>
                </div>
            </div>
        );
    }

    if (!user) {
        return (
            <div className="p-6 text-center">
                <p className="text-gray-500 dark:text-gray-400">Usuário não encontrado</p>
                <button
                    onClick={() => navigate('/users')}
                    className="mt-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                >
                    Voltar à lista
                </button>
            </div>
        );
    }

    return (
        <div className="h-full flex flex-col bg-white dark:bg-slate-800">
            {/* Header */}
            <div className="flex-shrink-0 border-b border-gray-200 dark:border-slate-700 p-4">
                <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-3">
                        <button
                            onClick={() => navigate('/users')}
                            className="p-2 text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
                            title="Voltar"
                        >
                            <IoMdArrowRoundBack size={18} />
                        </button>
                        <div>
                            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                                {user.name}
                            </h2>
                            <p className="text-sm text-gray-500 dark:text-gray-400">
                                {user.position} - {user.department}
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-y-auto p-6 space-y-6">
                {/* Informações Básicas */}
                <div className="bg-gray-50 dark:bg-slate-700 rounded-lg p-4">
                    <h3 className="font-semibold text-gray-900 dark:text-white mb-4 flex items-center space-x-2">
                        <FaUser className="text-blue-600" />
                        <span>Informações Básicas</span>
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="space-y-3">
                            <div className="flex items-center space-x-3">
                                <FaEnvelope className="text-gray-400" size={16} />
                                <div>
                                    <p className="text-xs text-gray-500 dark:text-gray-400">Email</p>
                                    <p className="text-sm text-gray-900 dark:text-white">{user.email}</p>
                                </div>
                            </div>
                            <div className="flex items-center space-x-3">
                                <FaPhone className="text-gray-400" size={16} />
                                <div>
                                    <p className="text-xs text-gray-500 dark:text-gray-400">Telefone</p>
                                    <p className="text-sm text-gray-900 dark:text-white">{user.phone}</p>
                                </div>
                            </div>
                        </div>
                        <div className="space-y-3">
                            <div>
                                <p className="text-xs text-gray-500 dark:text-gray-400">Perfil</p>
                                <p className="text-sm text-gray-900 dark:text-white">{getRoleLabel(user.role)}</p>
                            </div>
                            <div>
                                <p className="text-xs text-gray-500 dark:text-gray-400">Status</p>
                                <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium text-white ${getStatusColor(user.status)}`}>
                                    {user.status === 'ACTIVE' ? 'Ativo' : 'Inativo'}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Informações de Acesso */}
                <div className="bg-gray-50 dark:bg-slate-700 rounded-lg p-4">
                    <h3 className="font-semibold text-gray-900 dark:text-white mb-4 flex items-center space-x-2">
                        <FaClock className="text-green-600" />
                        <span>Informações de Acesso</span>
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="flex items-center space-x-3">
                            <FaCalendar className="text-gray-400" size={16} />
                            <div>
                                <p className="text-xs text-gray-500 dark:text-gray-400">Criado em</p>
                                <p className="text-sm text-gray-900 dark:text-white">{formatDate(user.createdAt)}</p>
                            </div>
                        </div>
                        <div className="flex items-center space-x-3">
                            <FaClock className="text-gray-400" size={16} />
                            <div>
                                <p className="text-xs text-gray-500 dark:text-gray-400">Último login</p>
                                <p className="text-sm text-gray-900 dark:text-white">{formatDate(user.lastLogin)}</p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Permissões */}
                <div className="bg-gray-50 dark:bg-slate-700 rounded-lg p-4">
                    <h3 className="font-semibold text-gray-900 dark:text-white mb-4">Permissões</h3>
                    <div className="flex flex-wrap gap-2">
                        {user.permissions.map((permission: string, index: number) => (
                            <span
                                key={index}
                                className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200"
                            >
                                {permission}
                            </span>
                        ))}
                    </div>
                </div>

                {/* Histórico de Atividades */}
                <div className="bg-gray-50 dark:bg-slate-700 rounded-lg p-4">
                    <h3 className="font-semibold text-gray-900 dark:text-white mb-4">Histórico Recente</h3>
                    <div className="space-y-3">
                        <div className="flex items-center space-x-3 p-3 bg-white dark:bg-slate-800 rounded-lg">
                            <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                            <div className="flex-1">
                                <p className="text-sm text-gray-900 dark:text-white">Login realizado</p>
                                <p className="text-xs text-gray-500 dark:text-gray-400">{formatDate(user.lastLogin)}</p>
                            </div>
                        </div>
                        <div className="flex items-center space-x-3 p-3 bg-white dark:bg-slate-800 rounded-lg">
                            <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                            <div className="flex-1">
                                <p className="text-sm text-gray-900 dark:text-white">Perfil atualizado</p>
                                <p className="text-xs text-gray-500 dark:text-gray-400">22/09/2024 14:30</p>
                            </div>
                        </div>
                        <div className="flex items-center space-x-3 p-3 bg-white dark:bg-slate-800 rounded-lg">
                            <div className="w-2 h-2 bg-yellow-500 rounded-full"></div>
                            <div className="flex-1">
                                <p className="text-sm text-gray-900 dark:text-white">Senha alterada</p>
                                <p className="text-xs text-gray-500 dark:text-gray-400">20/09/2024 09:15</p>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default UserPageView;
